// Developer: Ankit Gupta
/**
 * The credits pill in the top bar, and what opens from it: the balance, what was spent this month
 * (and how much of that went to Orion), the plan, and, inside an Orion conversation, what that
 * conversation has cost so far. Every figure is the account's own, from /credits/me; the
 * conversation's comes from the conversation already loaded on the page.
 */
import { useState } from 'react';
import { Button, Popover } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useT } from '@/i18n';
import { useIsRtl } from '@/hooks/useDirection';
import { browserOrUiLocale } from '@/utils/format';
import type { Balance } from '@/pages/Credits/types';

export default function CreditsPill({ balance }: { balance: Balance }) {
  const { t } = useT('shell');
  const rtl = useIsRtl();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const loc = browserOrUiLocale();
  const n = (v: number) => v.toLocaleString(loc);
  const month = balance.spent_this_month ?? 0;
  // the share of what the account had this month that is spent: spent / (spent + left)
  const pool = month + Math.max(0, balance.balance_credits);
  const pct = pool > 0 ? (month / pool) * 100 : 0;
  const threadId = /^\/orion\/(\d+)/.exec(location.pathname)?.[1];
  const conv = threadId ? (qc.getQueryData(['chat-thread', Number(threadId)]) as { usage?: { credits_total?: number } } | undefined) : undefined;
  const content = (
    <div className="dp-credits-pop">
      <div className="dp-credits-pop-head">
        <span>{t('creditsPop.title')}</span>
        {balance.plan && <span className="dp-credits-pop-plan">{balance.plan.name}</span>}
      </div>
      <div className="dp-credits-pop-row"><span>{t('creditsPop.available')}</span><strong>{t('creditsPop.cr', { value: n(balance.balance_credits) })}</strong></div>
      <div className="dp-credits-pop-row">
        <span>{t('creditsPop.month')}</span>
        <span>{t('creditsPop.cr', { value: n(month) })} ({pct.toLocaleString(loc, { maximumFractionDigits: 2 })}%)</span>
      </div>
      <div className="dp-credits-pop-bar" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}><span style={{ width: `${Math.min(100, Math.max(pct, month > 0 ? 4 : 0))}%` }} /></div>
      {(balance.orion_spent_this_month ?? 0) > 0 && (
        <div className="dp-credits-pop-row is-sub"><span>{t('creditsPop.orionMonth')}</span><span>{t('creditsPop.cr', { value: n(balance.orion_spent_this_month) })}</span></div>
      )}
      {conv?.usage?.credits_total != null && (
        <div className="dp-credits-pop-row is-split"><span>{t('creditsPop.conversation')}</span><strong>{t('creditsPop.credits', { count: conv.usage.credits_total, value: n(conv.usage.credits_total) })}</strong></div>
      )}
      <Button type="link" size="small" className="dp-credits-pop-more" onClick={() => { setOpen(false); navigate('/credits'); }}>{t('creditsPop.open')}</Button>
    </div>
  );
  return (
    <Popover open={open} onOpenChange={setOpen} trigger="click" placement={rtl ? 'bottomLeft' : 'bottomRight'} arrow={false} content={content} overlayClassName="dp-pop">
      <button type="button" className="dp-pill dp-pill-btn" aria-label={t('topbar.creditsTip')}>
        <ThunderboltOutlined className="dp-pill-bolt" />
        <span>
          <span className="dp-pill-label">{t('topbar.credits')}</span>
          <span className="dp-pill-value">{n(balance.balance_credits)}</span>
        </span>
      </button>
    </Popover>
  );
}
